import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography } from '../typography/Typography';
import { useStep } from './StepContext';
import { useProgressStepper } from './ProgressStepperContext';

/**
 * `StepLabel` contains the title and subtitle to be shown for each `Step` in a `ProgressStepper`.
 * It could also contain custom content provided inside a `StepContent` child.
 *
 * Related components: [ProgressStepper](#progressstepper), [Step](#step), [StepButton](#stepbutton),
 * [StepContent](#stepcontent).
 *
 * Usage:
 *
 * ```jsx
 * import { StepLabel } from '@one-thd/sui-atomic-components';
 * ```
 */
const StepLabel = ({
  title,
  subtitle,
  children,
  align: alignProp
}) => {

  const { align: stepAlignment, index, last } = useStep();
  let alignStep = stepAlignment;
  if (stepAlignment === 'start') {
    alignStep = 'left';
  } else if (stepAlignment === 'end') {
    alignStep = 'right';
  }
  const { isVertical } = useProgressStepper();
  const align = isVertical ? 'left' : (alignProp || alignStep);

  const firstHorizontal = !isVertical && index === 0;
  const lastHorizontal = !isVertical && last;
  const classes = classNames({
    'sui-pr-2': firstHorizontal,
    'sui-pl-2': lastHorizontal,
    'sui-px-2': !isVertical && !firstHorizontal && !lastHorizontal,
    'sui-ml-2 sui-mt-1': isVertical
  });
  return (
    <div className={classes}>
      {title && (
        <Typography weight="bold" align={align}>{title}</Typography>
      )}
      {subtitle && (
        <Typography variant="body-xs" color="subtle" align={align}>{subtitle}</Typography>
      )}
      {children}
    </div>
  );
};

StepLabel.displayName = 'StepLabel';

StepLabel.propTypes = {
  /**
   * The aligment of the component.
   */
  align: PropTypes.oneOf(['center', 'left', 'right']),
  /**
   * A `StepContent` component to inject custom content as part of a `Step`.
   */
  children: PropTypes.node,
  /**
   * A string or `Typography` component with the secondary title for the `Step` component.
   */
  subtitle: PropTypes.string,
  /**
   * A string or `Typography` component with the primary title for the `Step` component.
   */
  title: PropTypes.string
};

export { StepLabel };